import type { LocalViewId } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import type { Props, State } from './NavigationView.types.tsx';
import { withRefreshBlocker } from './withRefreshBlocker.tsx';
import { findView, updateContainerState } from './updateContainerState.tsx';

export const updateViewSet = (localId: LocalViewId, newName: string): Action<State, Props> =>
	withRefreshBlocker(
		async (
			{ setState, getState },
			{ navigationRemote, cloudId, containerId, onFailure, onSuccess },
		) => {
			if (
				!cloudId ||
				!containerId ||
				!navigationRemote ||
				getState().containers[containerId]?.isLoading ||
				!getState().containers[containerId]?.initialized
			) {
				return;
			}

			const { view } = findView(getState().containers[containerId], localId);

			if (!view || !view.viewType) {
				return;
			}

			experience.navBar.changeViewName.start();

			setState(
				updateContainerState(getState(), containerId, {
					views: [
						...getState().containers[containerId].views.map((innerView) =>
							innerView.localId === localId
								? { ...innerView, name: newName, isRenaming: false, isLocked: true }
								: innerView,
						),
					],
				}),
			);

			try {
				const updatedView = await navigationRemote.updateView({
					id: view.id,
					viewType: view.viewType,
					name: newName,
				});

				setState(
					updateContainerState(getState(), containerId, {
						views: [
							...getState().containers[containerId].views.map((innerView) =>
								innerView.localId === localId
									? { ...innerView, name: updatedView.name, isLocked: false }
									: innerView,
							),
						],
					}),
				);
				onSuccess?.('updateSection');

				experience.navBar.changeViewName.successWithReason({
					metadata: {
						isViewSet: true,
					},
				});
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				setState(
					updateContainerState(getState(), containerId, {
						views: [
							...getState().containers[containerId].views.map((innerView) =>
								innerView.localId === localId ? { ...innerView, isLocked: false } : innerView,
							),
						],
					}),
				);

				onFailure?.(error, 'updateSection');

				if (isClientFetchError(error)) {
					experience.navBar.changeViewName.abort(error);
					return;
				}

				experience.navBar.changeViewName.failure(error, {
					metadata: {
						isViewSet: true,
					},
				});
			}
		},
	);
